import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { RootState, useAppDispatch } from '../..'
import { EmployeeInfoResponse, ReviewRequestResponse } from '../../api/openapi'
import {
  AVAILABLE_EMPLOYEE_HIGHLIGHTS,
  ConstantIDs,
  EMPLOYEE_HIGHLIGHT_GROWTH_TALENT,
  EMPLOYEE_HIGHLIGHT_ICON_MAP,
  EMPLOYEE_HIGHLIGHT_ROCKSTAR,
} from '../../constants/employee'
import { useQuery } from '../../hooks/useQuery'
import { Employee, EmployeeKeys, EmployeeService, EmployeeSort } from '../../services/EmployeeService'
import { trackEvent, TrackingCategoryEnum, TrackingCategoryEventEnum } from '../../services/EventTrackingService'
import { PeriodService } from '../../services/PeriodService'
import { ReviewRequest, ReviewService } from '../../services/ReviewService'
import {
  finishImageIntroduction,
  hideImageIntroduction,
  showImageIntroductionId,
} from '../../slices/image-introduction-slice'
import { Period } from '../../types/definitions/Period'
import { SortOrderEnum } from '../../types/enums/sort-order.enum'
import {
  calculateEmployeeReviewPath,
  createPathToCreateNewReview,
  ReviewerTypeEnum,
} from '../../utils/employeePathUtils'
import { createPathWithLang } from '../../utils/languagePathUtils'
import { NetworkConstants } from '../../utils/NetworkConstants'
import { normalizeText } from '../../utils/text'
import { AppButton } from '../atoms/AppButton/AppButton'
import AppFilterButton from '../atoms/AppFilterButton/AppFilterButton'
import Breadcrumb, { BreadcrumbItem } from '../atoms/Breadcrumb/Breadcrumb'
import { TableIcon } from '../atoms/Icons/TableIcon'
import Spinner from '../atoms/Spinner'
import TextLine from '../atoms/TextLine'
import ErrorModal from '../molecules/ErrorModal'
import { IntroductionImagesModal } from '../molecules/IntroductionImagesModal'
import { PeriodCounter } from '../molecules/PeriodCounter'
import { TutorialModal } from '../molecules/TutorialModal'
import { RequestedReviewTable } from '../organisms/RequestedReviewTable'
import { ReviewTable } from '../organisms/ReviewTable'
import TeamChart from '../organisms/TeamChart'

export interface Filter {
  property: string
  value: string
}

interface FilteredResults {
  employees: Employee[]
  reviewRequests: ReviewRequest[]
}

export type SectionType = 'table' | 'diagram'

const stripResultCount = (label: string) =>
  label.endsWith(' results)') || label.endsWith(' result)') ? label.slice(0, label.lastIndexOf(' (')) : label

export const ReviewsPage = (): JSX.Element => {
  const user = useSelector<RootState, Employee | null>(state => state.auth.user)
  const history = useHistory()
  const query = useQuery()
  const dispatch = useAppDispatch()
  const [selectedLeaders, setSelectedLeaders] = useState<number[]>([])
  const [breadcrumbData, setBreadcrumbData] = useState<BreadcrumbItem[]>([])
  const [employees, setEmployees] = useState<Employee[]>([])
  const [myEmployees, setMyEmployees] = useState<Employee[]>([])
  const [tableEmployees, setTableEmployees] = useState<Employee[]>([])
  const [searchedEmployees, setSearchedEmployees] = useState<Employee[]>([])
  const [filteredAndSortedEmployees, setFilteredAndSortedEmployees] = useState<Employee[]>([])
  const [reviewRequests, setReviewRequests] = useState<ReviewRequest[]>([])
  const [searchedReviewRequests, setSearchedReviewRequests] = useState<ReviewRequest[]>([])
  const [filteredAndSortedReviewRequests, setFilteredAndSortedReviewRequests] = useState<ReviewRequest[]>([])
  const [textSearch, setTextSearch] = useState<string>('')
  const [enabledFilter, setEnabledFilter] = useState<Filter | undefined>({ property: 'direct', value: 'My Team' })
  const [selectedSort, setSelectedSort] = useState<EmployeeSort[]>([
    { property: 'status', order: SortOrderEnum.Ascending },
  ])
  const [selectedReviewRequestSort, setSelectedReviewRequestSort] = useState<EmployeeSort[]>([
    { property: 'reviewedEmployee.name', order: SortOrderEnum.Ascending },
  ])
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [activeSection, setActiveSection] = useState<SectionType>(query.get('view') === 'diagram' ? 'diagram' : 'table')
  const [totalTodoReviews, setTotalTodoReviews] = useState(0)
  const [completedReviews, setCompletedReviews] = useState(0)
  const [percentageOfCompletedReviews, setPercentageOfCompletedReviews] = useState(0)
  const [currentPeriod, setCurrentPeriod] = useState<Period>()
  const [loadedAllEmployees, setLoadedAllEmployees] = useState(false)
  const [suggestions, setSuggestions] = useState<EmployeeInfoResponse[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  // Introduction tutorial
  const tutorialCompleted = useSelector<RootState, boolean>(state => state.imageIntroduction.completed)
  const [introductionView, setIntroductionView] = useState<boolean>(tutorialCompleted)

  useEffect(() => {
    _loadPeriod()
    dispatch(hideImageIntroduction())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadData = async () => {
    setLoading(true)
    _loadReviewRequests()
    await _loadMyEmployees()
    setLoading(false)
    setLoadedAllEmployees(true)
  }

  useEffect(() => {
    currentPeriod && loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPeriod])

  useEffect(() => {
    if (activeSection === 'diagram' && !loadedAllEmployees && currentPeriod) {
      _loadAllEmployees(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, loadedAllEmployees, currentPeriod])

  // TODO expand filter complexity to cater for other non-status based filters
  useEffect(() => {
    if (!enabledFilter) {
      setFilteredAndSortedEmployees(EmployeeService.sortEmployees(searchedEmployees, selectedSort))
      setFilteredAndSortedReviewRequests(ReviewService.sortReviewRequests(reviewRequests, selectedSort))
    } else {
      const filteredResults: FilteredResults = {
        employees,
        reviewRequests,
      }
      if (enabledFilter.property === 'direct') {
        filteredResults.employees = tableEmployees
      } else if (enabledFilter.property === 'search') {
        filteredResults.employees = searchedEmployees
        filteredResults.reviewRequests = searchedReviewRequests
      } else if (enabledFilter.property === 'highlights') {
        filteredResults.employees = searchedEmployees.filter(
          // @ts-expect-error FIXME
          employee => employee[enabledFilter.property]?.some(h => h === enabledFilter.value)
        )
        filteredResults.reviewRequests = reviewRequests.filter(request =>
          request.reviewedEmployee.highlights?.some(h => h === enabledFilter.value)
        )
      } else {
        filteredResults.employees = searchedEmployees.filter(
          // @ts-expect-error FIXME
          employee => employee[enabledFilter.property] === enabledFilter.value
        )
        filteredResults.reviewRequests = reviewRequests
      }
      setFilteredAndSortedEmployees(EmployeeService.sortEmployees(filteredResults.employees, selectedSort))
      setFilteredAndSortedReviewRequests(ReviewService.sortReviewRequests(filteredResults.reviewRequests, selectedSort))
      if (!selectedLeaders.length) {
        const breadcrumbData = [
          { label: `${enabledFilter ? enabledFilter?.value : 'All'} (${filteredResults.employees.length} results)` },
        ]
        setBreadcrumbData([...breadcrumbData])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedEmployees, searchedReviewRequests, enabledFilter, selectedLeaders])

  useEffect(() => {
    trackEvent(TrackingCategoryEnum.REVIEW_LIST, TrackingCategoryEventEnum.REVIEW_LIST.SEARCH_CHANGED, '')
    if (textSearch.length === 0 && !loading) {
      setSuggestions([])
      setShowSuggestions(false)
      // TODO: WE NEED TO IMPROVE VIEW REACTION CONTROL (REFACTORING COMPONENTS)
      if (enabledFilter?.property === undefined && breadcrumbData[0].label.includes('Search')) return
      if (enabledFilter?.property === 'search') {
        setEnabledFilter({ property: 'direct', value: 'My Team' })
        resetBreadcrumbData()
      }
      return
    }

    if (textSearch.length < 2) return

    // Pre-load full hierarchy in the background the first time the user types,
    // so the complete pool is ready for both autocomplete and search results
    if (!employees.length) {
      EmployeeService.findAllWithAccess(undefined, currentPeriod, undefined, undefined, undefined, user as Employee)
        .then(fetched => setEmployees(fetched))
        .catch(console.error)
    }

    const timer = setTimeout(() => {
      // Use the full hierarchy pool when available, fall back to immediate team while it loads
      const searchPool = employees.length ? employees : myEmployees
      const results = searchPool
        .filter(e => normalizeText(e.name ?? '').includes(normalizeText(textSearch)))
        .map(e => ({ id: e.id, name: e.name })) as EmployeeInfoResponse[]
      setSuggestions(results)
      setShowSuggestions(results.length > 0)
    }, 300)

    return () => {
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textSearch, loading])

  useEffect(() => {
    const reviewsToBeDone = reviewRequests.length + myEmployees.length
    setTotalTodoReviews(reviewsToBeDone)
    const completedReviewRequests = reviewRequests.filter(
      rr => rr.status === 'Completed' || rr.status === 'Skipped'
    ).length
    const completedTeamReviews = myEmployees.filter(rr => rr.status === 'Completed' || rr.status === 'Skipped').length
    const finishedReviews = completedReviewRequests + completedTeamReviews
    setCompletedReviews(finishedReviews)
    if (reviewsToBeDone === 0) {
      setPercentageOfCompletedReviews(0)
    } else {
      setPercentageOfCompletedReviews(Math.round((finishedReviews / reviewsToBeDone) * 100))
    }
  }, [myEmployees, reviewRequests])

  const resetBreadcrumbData = () => {
    setSelectedLeaders([])
    const breadcrumbData = [{ label: `All (${employees.length} results)` }]
    setBreadcrumbData([...breadcrumbData])
  }

  useEffect(() => {
    setFilteredAndSortedEmployees(() => EmployeeService.sortEmployees(filteredAndSortedEmployees, selectedSort))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSort])

  useEffect(() => {
    setFilteredAndSortedReviewRequests(() =>
      ReviewService.sortReviewRequests(filteredAndSortedReviewRequests, selectedReviewRequestSort)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReviewRequestSort])

  const _loadAllEmployees = async (handleLoading = true): Promise<Employee[] | undefined> => {
    if (!employees.length) {
      handleLoading && setLoading(true)
      try {
        // Limit depth to improve performance. Higher depth can be loaded on demand if needed.
        const DEFAULT_DEPTH = 2
        const employeesResponse = await EmployeeService.findAllWithAccess(
          DEFAULT_DEPTH,
          currentPeriod,
          undefined,
          undefined,
          undefined,
          user as Employee
        )
        setEmployees(employeesResponse)
        setSearchedEmployees(employeesResponse)
        return employeesResponse
      } catch (error) {
        console.error(error)
      } finally {
        trackEvent(TrackingCategoryEnum.REVIEW_LIST, TrackingCategoryEventEnum.REVIEW_LIST.PAGE_ON_LOAD, '')
        setLoadedAllEmployees(true)
        handleLoading && setLoading(false)
      }
    } else {
      setSearchedEmployees(employees)
      return employees
    }
  }

  const _loadReviewRequests = async () => {
    if (!reviewRequests.length) {
      try {
        const reviewRequests = await ReviewService.getReviewRequests()
        setReviewRequests(reviewRequests)
        setSearchedReviewRequests(reviewRequests)
        setFilteredAndSortedReviewRequests(reviewRequests)
      } catch (error) {
        console.error(error)
      }
    }
  }

  const _loadLeaders = async (employeeId?: number) => {
    if (employeeId === user?.id) {
      handleDirectFilterSelected()
    } else {
      if (employeeId) {
        const idx = selectedLeaders.indexOf(employeeId)
        setSelectedLeaders(selectedLeaders.slice(0, idx + 1))
      }

      const leadersIds = selectedLeaders[0] === user?.id ? selectedLeaders.slice(1) : selectedLeaders
      const lastLeaderId = employeeId ?? leadersIds.at(-1) ?? (user?.id as number)
      const filteredEmployees = await EmployeeService.findDirectUnder(lastLeaderId, user as Employee, currentPeriod)

      setTableEmployees(filteredEmployees)
      setSearchedEmployees(filteredEmployees)
      setBreadcrumbData(prev => {
        const crumbs = employeeId ? prev.slice(0, prev.findIndex(bc => bc.id === employeeId) + 1) : [...prev]
        const last = crumbs[crumbs.length - 1]
        const baseLabel = stripResultCount(last.label)
        crumbs[crumbs.length - 1] = { ...last, label: `${baseLabel} (${filteredEmployees.length} results)` }
        return crumbs
      })
    }
  }

  const _loadPeriod = async () => {
    const period = await PeriodService.findActive()
    setCurrentPeriod(period)
  }

  const _loadMyEmployees = async () => {
    const myEmployees = await EmployeeService.findAllWithAccess(
      0,
      currentPeriod,
      undefined,
      undefined,
      undefined,
      user as Employee
    )
    setMyEmployees(myEmployees)
    handleDirectFilterSelected(myEmployees)
  }

  const handleLeadersSearch = (employeeId?: number) => {
    if (!employeeId) {
      if (!selectedLeaders.includes(user?.id as number)) selectedLeaders.push(user?.id as number)
    } else {
      if (enabledFilter?.property === 'direct') {
        if (!selectedLeaders.includes(user?.id as number)) selectedLeaders.push(user?.id as number)
        if (!selectedLeaders.includes(employeeId)) selectedLeaders.push(employeeId)
      } else {
        if (!selectedLeaders.includes(employeeId)) selectedLeaders.push(employeeId)
      }
    }
    _loadLeaders()
  }

  const trackTabSelected = (tab: string) => {
    trackEvent(
      TrackingCategoryEnum.REVIEW_LIST,
      TrackingCategoryEventEnum.REVIEW_LIST.CLICK_NAVIGATION_TABS,
      JSON.stringify({ selectedTab: tab })
    )
  }

  const handleDirectFilterSelected = async (_myEmployees?: Employee[]) => {
    const myTeam = _myEmployees || myEmployees
    setTableEmployees(myTeam)
    trackTabSelected('My Team')
    setEnabledFilter({ property: 'direct', value: 'My Team' })
    setTextSearch('')
    setBreadcrumbData([{ label: `My Team (${myTeam.length} results)`, id: user?.id as number }])
    setSelectedLeaders([user?.id as number])
  }

  const handleHighlightFilterSelected = (highlight: string) => async () => {
    trackTabSelected(highlight)
    setSelectedLeaders([])
    setLoading(true)
    try {
      const highlightsMap: Record<string, boolean> = {
        tenX: highlight === EMPLOYEE_HIGHLIGHT_ROCKSTAR,
        nineX: highlight === EMPLOYEE_HIGHLIGHT_GROWTH_TALENT,
      }
      const filteredEmployees = await EmployeeService.findAllWithAccess(
        0,
        currentPeriod,
        undefined,
        highlightsMap.tenX || undefined,
        highlightsMap.nineX || undefined,
        user as Employee
      )
      setSearchedEmployees(filteredEmployees)
      setEnabledFilter({ property: 'highlights', value: highlight })
      setTextSearch('')
      setBreadcrumbData([{ label: `${highlight} (${filteredEmployees.length} results)` }])
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const searchResults = async (signal?: AbortSignal, query?: string): Promise<void> => {
    const searchQuery = query ?? textSearch
    setSelectedLeaders([])
    setEnabledFilter({ property: 'search', value: 'Search' })
    const filterItem = (item: string, matchValue: string) => normalizeText(item).includes(normalizeText(matchValue))

    setLoading(true)
    try {
      // Load the full accessible hierarchy on first search, then cache it in `employees` for subsequent searches
      let searchPool = employees
      if (!searchPool.length) {
        const fetched = await EmployeeService.findAllWithAccess(
          undefined,
          currentPeriod,
          undefined,
          undefined,
          undefined,
          user as Employee,
          signal
        )
        setEmployees(fetched)
        searchPool = fetched
      }
      const results = searchPool.filter(e => filterItem(e.name ?? '', searchQuery))
      setSearchedEmployees(results)
      setBreadcrumbData([{ label: `Search: ${searchQuery} (${results.length} results)` }])
    } catch (error: any) {
      if (error?.name !== 'CanceledError' && error?.name !== 'AbortError') console.error(error)
    } finally {
      setLoading(false)
    }

    setSearchedReviewRequests(
      reviewRequests.filter(reviewRequest => filterItem(reviewRequest.reviewedEmployee.name, searchQuery))
    )
  }

  const handleClear = (): void => {
    setTextSearch('')
    setSuggestions([])
    setShowSuggestions(false)
  }

  const handleSearchSubmit = (): void => {
    setShowSuggestions(false)
    setSuggestions([])
    searchResults()
  }

  const handleSuggestionClick = (suggestion: EmployeeInfoResponse): void => {
    setTextSearch(suggestion.name)
    setShowSuggestions(false)
    setSuggestions([])
    searchResults(undefined, suggestion.name)
  }

  const handleSortSelected = (property: string, order: SortOrderEnum): void => {
    setSelectedSort([{ property: property as EmployeeKeys, order }])
  }

  const handleReviewRequestSortSelected = (property: string, order: SortOrderEnum): void => {
    setSelectedReviewRequestSort([{ property: property, order }])
  }

  const handleOnEmployeeClicked = (employee: Employee) => calculateEmployeeReviewPath(employee, user as Employee)

  const handleNewReview = (employee: Employee) => createPathToCreateNewReview(employee.id)

  const handleOnReviewRequestClicked = (reviewRequest: ReviewRequestResponse): string =>
    createPathWithLang(
      NetworkConstants.URL_EDIT_REVIEW.replace(
        ConstantIDs.REVIEW_ID_PATH_PLACEHOLDER,
        reviewRequest.review.id.toString()
      )
    )

  const handleOnReviewRequestRowClicked = (reviewRequest: ReviewRequestResponse): void => {
    const path = calculateEmployeeReviewPath(
      reviewRequest.reviewedEmployee as Employee,
      user as Employee,
      ReviewerTypeEnum.REQUESTED
    )
    history.push(path)
  }

  const handleOnHierarchyNavigate = (employee: Employee): void => {
    if (enabledFilter?.property !== 'direct') setEnabledFilter(undefined)
    setBreadcrumbData(prev => {
      const updated = [...prev]
      const last = updated[updated.length - 1]
      const strippedLabel = stripResultCount(last.label)
      updated[updated.length - 1] = { ...last, label: strippedLabel }
      return [...updated, { label: employee.name, id: employee.id }]
    })
    handleLeadersSearch(employee.id)
    setTextSearch('')
    trackEvent(
      TrackingCategoryEnum.REVIEW_LIST,
      TrackingCategoryEventEnum.REVIEW_LIST.CLICK_HIERARCHY_ICON,
      JSON.stringify({ selectedEmployeeId: employee.id })
    )
  }

  const handleLeadersNavigation = (employeeId: number): void => {
    _loadLeaders(employeeId)
  }

  const navigateToSection = (section: SectionType): void => {
    if (!section) return

    setActiveSection(section)
    query.set('view', section)
    history.replace({
      search: query.toString(),
    })
  }

  const statusesCount: Record<string, number> = {}
  const highlightsCount: Record<string, number> = {}

  const employeesToCount = employees.length > 0 ? employees : myEmployees
  employeesToCount.forEach(employee => {
    if (employee.status) {
      statusesCount[employee.status] = (statusesCount[employee.status] ?? 0) + 1
    }

    if (employee.highlights) {
      employee.highlights.forEach(highlight => (highlightsCount[highlight] = (highlightsCount[highlight] ?? 0) + 1))
    }
  })

  const TutorialIntroduction = () => {
    return (
      <div className="container flex justify-center my-auto">
        <div className="flex flex-col justify-center">
          <div className="w-full text-center leading-[3rem]">
            <p className="text-6xl font-medium mb-16">Welcome to Grapes</p>
            <div className="text-3xl font-normal mb-12 flex flex-col gap-8">
              <p>
                The platform is divided into different areas to help you assess your team members, analyze individual or
                team profiles, spot issues, and find suitable resources for new projects.
              </p>
              <p>Let's have a quick tour to get you started!</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handleTable = () => {
    trackEvent(TrackingCategoryEnum.REVIEW_LIST, TrackingCategoryEventEnum.REVIEW_LIST.CLICK_TABLE_BTN)
    navigateToSection('table')
  }

  if (loading) {
    return <Spinner />
  }
  return (
    <div className="px-4 flex-grow flex flex-col">
      <div className="container mt-10 flex flex-wrap">
        <TutorialModal
          visible={!introductionView}
          onClose={() => {
            setIntroductionView(true)
            dispatch(showImageIntroductionId(0))
          }}
          skipTutorial={() => {
            setIntroductionView(true)
            dispatch(finishImageIntroduction())
          }}
          icon={false}
          startButtonText="Quick Tutorial"
          message={<TutorialIntroduction />}
        />
        {/* This images tutorial should be temporary. If needed to return to the other tutorial format, search before 15/12/22*/}
        <IntroductionImagesModal visible={!tutorialCompleted && introductionView} />
        <div className="flex flex-col gap-6">
          <h1 id="title" className=" text-6xl text-dark-blue font-semibold">
            Assessments
          </h1>
          <label className="text-xl font-bold align-left">
            Progress: ({completedReviews}/{totalTodoReviews}) · {percentageOfCompletedReviews}%
          </label>
        </div>
        <div className="flex flex-col items-center align-middle mx-auto gap-4">
          <div className="relative" onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}>
            <TextLine
              disabled={!loadedAllEmployees}
              className="text-border border-orange chooseInput"
              type="text"
              value={textSearch}
              searchIcon={true}
              onChange={value => {
                setTextSearch(value)
              }}
              onClear={handleClear}
              onSubmit={handleSearchSubmit}
              placeholder={loadedAllEmployees ? 'Quick find any team member' : 'Loading all employees...'}
            />
            {showSuggestions && (
              <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-y-auto">
                {suggestions.map(s => (
                  <li
                    key={s.id}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm flex justify-between items-center"
                    onMouseDown={() => handleSuggestionClick(s)}
                  >
                    <span>{s.name}</span>
                    {s.role && <span className="text-gray-400 text-xs ml-2">{s.role}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <PeriodCounter currentPeriod={currentPeriod} />
      </div>
      <div className="container mt-14 mb-4 relative flex flex-col gap-10">
        <div className={classNames('flex', { 'justify-end': activeSection === 'diagram' })}>
          {activeSection === 'table' && (
            <div className="flex flex-1 flex-wrap gap-4">
              {/* lambda function below to avoid passing the event object to handleDirectFilterSelected */}
              <AppFilterButton
                active={enabledFilter?.property === 'direct'}
                onClick={() => handleDirectFilterSelected()}
              >
                My Team
              </AppFilterButton>
              {AVAILABLE_EMPLOYEE_HIGHLIGHTS.map(highlight => {
                const highlightValue = highlightsCount[highlight.value]
                return (
                  <AppFilterButton
                    key={highlight.value}
                    active={enabledFilter?.value === highlight.value}
                    onClick={handleHighlightFilterSelected(highlight.value)}
                  >
                    {EMPLOYEE_HIGHLIGHT_ICON_MAP[highlight.value]}&nbsp;&nbsp;{highlight.label}
                    {!!highlightValue ? ` (${highlightValue})` : null}
                  </AppFilterButton>
                )
              })}
            </div>
          )}
          <div className="admin-button-container">
            {activeSection === 'diagram' ? (
              <AppButton type="clear" onClick={handleTable}>
                <TableIcon />
                <span>Table</span>
              </AppButton>
            ) : null}
          </div>
        </div>
      </div>
      {activeSection === 'table' &&
      ['direct', 'search', 'highlights'].some(filter => enabledFilter?.property === filter) &&
      filteredAndSortedReviewRequests.length > 0 ? (
        <>
          <div className="container mt-6 mb-4 relative flex flex-col gap-10">
            <Breadcrumb
              items={[{ label: `My requests (${filteredAndSortedReviewRequests.length} results)` }]}
              onItemSelected={handleLeadersNavigation}
            />
          </div>
          <div className="container mb-16">
            <RequestedReviewTable
              selectedSort={selectedReviewRequestSort[0]}
              onSortSelected={handleReviewRequestSortSelected}
              requests={filteredAndSortedReviewRequests}
              handleOnEmployeeClicked={handleOnReviewRequestClicked}
              handleOnRequestedRowClicked={handleOnReviewRequestRowClicked}
              handleNewReview={handleNewReview}
            />
          </div>
        </>
      ) : null}
      {activeSection === 'table' && breadcrumbData.length ? (
        <div className="container mt-6 mb-4 relative flex flex-col gap-10">
          <Breadcrumb items={breadcrumbData} onItemSelected={handleLeadersNavigation} />
        </div>
      ) : null}
      <div className="container flex-grow">
        {activeSection === 'table' && (
          <>
            <ReviewTable
              employees={filteredAndSortedEmployees}
              selectedSort={selectedSort[0]}
              handleNewReview={handleNewReview}
              onHierarchyNavigate={handleOnHierarchyNavigate}
              handleOnEmployeeClicked={handleOnEmployeeClicked}
              onSortSelected={handleSortSelected}
            />
          </>
        )}
        {activeSection === 'diagram' && (
          <TeamChart
            employees={employees}
            highlight={textSearch ? filteredAndSortedEmployees.map(employee => employee.id) : undefined}
            currentUser={user}
          />
        )}
      </div>
      {showErrorModal && (
        <ErrorModal
          visible={showErrorModal}
          message={<p>Employee selected was not found</p>}
          onClose={() => setShowErrorModal(false)}
        />
      )}
    </div>
  )
}

export default ReviewsPage
